import { useRef } from "react";
import { Coffee, Award, Milk, Wine, Gift, MapPin } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const cards = [
  {
    id: "cafes",
    title: "Cafés especiais em três níveis",
    body: "Tradicional, Gourmet ou Especial — incluindo o raro Café do Jacu, moído na hora para máximo aroma.",
    eyebrow: "01 · Curadoria",
    icon: Coffee,
    image: "/curadoria-v2.avif",
    span: "md:col-span-4 md:row-span-2",
    tone: "dark",
  },
  {
    id: "premiados",
    title: "Produtos premiados",
    body: "Sempre há ao menos um produto premiado no Brasil ou no exterior nas nossas prateleiras.",
    eyebrow: "02 · Prêmios",
    icon: Award,
    span: "md:col-span-2 md:row-span-1",
    tone: "gold",
  },
  {
    id: "canastra",
    title: "Queijo Canastra legítimo",
    body: "Da Serra da Canastra, Cruzília e Alagoa — com origem e sabor que só Minas tem.",
    eyebrow: "03 · Origem",
    icon: Milk,
    image:
      "https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?auto=format&fit=crop&w=1200&q=80",
    span: "md:col-span-2 md:row-span-1",
    tone: "image",
  },
  {
    id: "cachaca",
    title: "Cachaças premiadas",
    body: "Campeãs na ExpoCachaça, incluindo a primeira aguardente de café do Brasil.",
    eyebrow: "04 · ExpoCachaça",
    icon: Wine,
    span: "md:col-span-2 md:row-span-1",
    tone: "cream",
  },
  {
    id: "kits",
    title: "Kits e cestas mineiras",
    body: "Presentes montados com produtos selecionados — perfeitos para levar Minas na mala.",
    eyebrow: "05 · Presentear",
    icon: Gift,
    span: "md:col-span-2 md:row-span-1",
    tone: "dark",
  },
  {
    id: "local",
    title: "No coração de Alfenas",
    body: "Praça Getúlio Vargas, atrás da Igreja Matriz — ponto de referência no centro histórico.",
    eyebrow: "06 · Endereço",
    icon: MapPin,
    span: "md:col-span-2 md:row-span-1",
    tone: "cream",
  },
];

const toneClasses = {
  dark: "bg-espresso text-cream border-cream/10",
  gold: "bg-gold text-espresso border-espresso/10",
  cream: "bg-cream-50 text-espresso border-espresso/5",
  image: "text-cream border-cream/10",
};

export default function Differentials() {
  const containerRef = useRef();

  useGSAP(() => {
    let mm = gsap.matchMedia();

    mm.add("(max-width: 1023px)", () => {
      const cardsArray = gsap.utils.toArray(".bento-card-stack");
      
      // Pin the section itself once cards are done
      ScrollTrigger.create({
        trigger: "#diferenciais",
        start: "bottom bottom",
        end: "+=100%", // Extra space for following sections to overlap
        pin: true,
        pinSpacing: false,
      });

      cardsArray.forEach((card, i) => {
        // Pinning the card
        ScrollTrigger.create({
          trigger: card,
          start: "top 100px",
          pin: true,
          pinSpacing: false,
          endTrigger: "#diferenciais",
          end: "bottom 100px",
        });

        // Scale and fade effect as the NEXT card approaches
        // Start later (top 40%) so blur doesn't come too early
        if (i < cardsArray.length - 1) {
          gsap.to(card, {
            scale: 0.9,
            opacity: 0.4,
            filter: "blur(8px)",
            scrollTrigger: {
              trigger: cardsArray[i + 1],
              start: "top 60%", 
              end: "top 120px",
              scrub: true,
            }
          });
        }
      });
    });

    return () => mm.revert();
  }, { scope: containerRef });

  return (
    <section
      id="diferenciais"
      ref={containerRef}
      data-testid="differentials-section"
      className="relative bg-cream py-24 md:py-36 z-0"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-end mb-14 md:mb-20">
          <div className="md:col-span-7 reveal-left">
            <div className="eyebrow text-gold mb-5 reveal-eyebrow">O que nos torna únicos</div>
            <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[1.02] tracking-[-0.02em] text-espresso max-w-[16ch] reveal-clip">
              Seis razões para atravessar <em className="italic text-gold">Minas</em>.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 reveal-right">
            <p className="text-espresso/70 text-base md:text-[17px] leading-relaxed">
              Cada item à venda passa por uma curadoria obsessiva. O que chega
              até a sua mesa representa o melhor do sul de Minas — do primeiro
              aroma ao último gole.
            </p>
          </div>
        </div>

        <div className="bento-cards-container flex flex-col md:grid md:grid-cols-6 md:grid-rows-3 gap-5 md:gap-6">
          {cards.map((c, i) => {
            const Icon = c.icon;
            const isImage = c.tone === "image";
            return (
              <article
                key={c.id}
                data-testid={`bento-card-${c.id}`}
                className={`bento-card bento-card-stack ${toneClasses[c.tone]} ${c.span} p-7 md:p-8 flex flex-col justify-between border relative group rounded-[14px] min-h-[320px] md:min-h-0 mb-[10vh] lg:mb-0`}
                style={{
                  zIndex: i + 1,
                  ...(c.tone === "dark" && c.image
                    ? {
                        backgroundImage: `linear-gradient(to top, rgba(26,15,7,0.92) 0%, rgba(26,15,7,0.55) 55%, rgba(26,15,7,0.35) 100%), url(${c.image})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }
                    : isImage
                      ? {
                          backgroundImage: `linear-gradient(to top, rgba(26,15,7,0.85) 0%, rgba(26,15,7,0.25) 60%, rgba(26,15,7,0.1) 100%), url(${c.image})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }
                      : {}),
                }}
              >
                <div className="flex items-start justify-between">
                  <span
                    className={`font-mono text-[10px] tracking-[0.3em] uppercase ${
                      c.tone === "gold" ? "text-espresso/70" : c.tone === "cream" ? "text-espresso/60" : "text-cream/60"
                    }`}
                  >
                    {c.eyebrow}
                  </span>
                  <Icon
                    size={22}
                    className={`${
                      c.tone === "gold" ? "text-espresso" : c.tone === "cream" ? "text-gold" : "text-gold"
                    } transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110`}
                  />
                </div>
                <div className="mt-8">
                  <h3 className="font-display text-[26px] md:text-[32px] leading-[1.05] tracking-tight max-w-[20ch]">
                    {c.title}
                  </h3>
                  <p
                    className={`mt-4 text-sm md:text-[15px] leading-relaxed max-w-[42ch] ${
                      c.tone === "gold"
                        ? "text-espresso/80"
                        : c.tone === "cream"
                          ? "text-espresso/70"
                          : "text-cream/75"
                    }`}
                  >
                    {c.body}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
